import type { PublicationCode } from '@/scopes/common/domain/types'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { getSearchPageUSPs } from '@/scopes/search/search/domain/policies/publicationCodes'
import { mqMax, space, tok } from '@sunwebgroup/ui-components'
import { Typography } from '@sunwebgroup/ui-components/uds'
import { css, styled } from 'styled-components'
import * as List from './ListItem'

export interface Usp {
  translation: string
  link?: string
}

interface UspsProps {
  uspVisibleOnMobile: number
}

const StyledUsps = styled.aside`
  color: ${tok('semantics_content_color')};
  gap: ${space('primitives_space_16')};
`

const StyledBulletList = styled(List.Root)`
  justify-content: space-evenly;
  display: flex;
  flex-wrap: wrap;
`

const StyledBulletListItem = styled(List.Item)<{ $visibleOnMobile: boolean }>`
  display: flex;
  text-align: center;
  align-items: center;
  ${({ $visibleOnMobile }) =>
    !$visibleOnMobile
    && css`
      @media ${mqMax('primitives_breakpoint_s_max')} {
        display: none !important;
      }
    `}
`

const UspLink = styled.a`
  padding: 0;
  min-height: 0;
  text-decoration: none;
  ${Typography.modVariant('body-m-regular-tight')}

  &:hover {
    color: inherit;
  }
`

export function Usps({ uspVisibleOnMobile }: UspsProps) {
  const intl = useIntl()
  // TODO: For now we hardcoded to get summer catalogue, but when we have winter search page we need to change this
  const publicationCode = useConfiguration('publicationCode')
  const usps = getSearchPageUSPs(publicationCode as PublicationCode) // TODO change this `as` when configurations are complete

  if (!usps) {
    return null
  }

  return (
    <StyledUsps>
      <StyledBulletList>
        {usps.map((usp, index) => (
          <StyledBulletListItem
            $size={24}
            key={`usp_${index}`}
            $visibleOnMobile={index === uspVisibleOnMobile - 1}
            data-qa={`usp_${index + 1}`}
          >
            {usp.link
              ? (
                  <UspLink href={usp.link} target="_blank">
                    {intl.formatMessage({ id: usp.translation })}
                  </UspLink>
                )
              : (
                  intl.formatMessage({ id: usp.translation })
                )}
          </StyledBulletListItem>
        ))}
      </StyledBulletList>
    </StyledUsps>
  )
}
